import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Store } from '@ngrx/store';

import { MerchantBasicDataViewModel } from '@app/api/marketplace/models';
import { MerchantActions } from '@merchant/store/merchant.slice';
import { EasyAddressSubformComponent } from '@shared/components/easy-address-subform/easy-address-subform.component';
import { required } from '@shared/decorators/required.decorator';
import {
  getServerErrorForField,
  processErrorResponse,
} from '@shared/helpers/form-helper';
import { trimInput } from '@shared/helpers/input-trim-helper';
import { ApiErrorResponse } from '@shared/interfaces/api-error-response';
import { FileUpload } from '@shared/interfaces/merchant-shared';
import { packingMaterialRegistrations } from '@shared/interfaces/packing-material-registrations';
import { DialogService } from '@shared/services/dialog.service';
import { isValidEmail } from '@shared/validators/email.validator';
import { isPhoneNumber } from '@shared/validators/phone-number.validator';
import { isTaxNumber } from '@shared/validators/tax-number.validator';

@Component({
  selector: 'app-merchant-basic-data-form',
  templateUrl: './merchant-basic-data-form.component.html',
  styleUrls: ['./merchant-basic-data-form.component.scss'],
})
export class MerchantBasicDataFormComponent {
  @Input() set basicData(data: MerchantBasicDataViewModel | undefined | null) {
    if (data) {
      this.onData(data);
      this.firstLogin = !data?.businessAddress?.zipCode;
    }
  }
  @Input() @required isLoading!: boolean;
  @Input() logoUrl: string | null | undefined;
  @Input() isLogoUpdating!: boolean;
  @Input() @required set updating(done: boolean) {
    if (done) {
      this.form.markAsPristine();
    }
  }

  @Input() @required set error(data: ApiErrorResponse | null | undefined) {
    processErrorResponse(this.form, data);
  }

  @Output() submitData = new EventEmitter<{
    formValue: any;
    firstLogin: boolean;
  }>();
  @Output() cancel = new EventEmitter();

  TITLE = 'MERCHANT.TITLE.';
  PLACEHOLDER = 'MERCHANT.PLACEHOLDER.';
  HINT = 'MERCHANT.HINT.';
  REQUIRED = this.HINT + 'REQUIRED';

  readonly packingMaterialRegistrations = packingMaterialRegistrations;

  showVisibleFields = false;
  firstLogin = false;

  form: UntypedFormGroup = this.fb.group({
    companyName: new UntypedFormControl(null, [Validators.required]),
    brandName: new UntypedFormControl(null, [Validators.required]),
    taxNumber: new UntypedFormControl(null, [Validators.required, isTaxNumber]),
    email: new UntypedFormControl(null, [Validators.required, isValidEmail]),
    phone: new UntypedFormControl(null, [Validators.required, isPhoneNumber]),
    businessAddress: EasyAddressSubformComponent.buildAddressForm(this.fb),
    vatExempt: new UntypedFormControl(false),
    gs1Partner: new UntypedFormControl(false),
    commentsEnabled: new UntypedFormControl(true, [Validators.required]),
    rankingEnabled: new UntypedFormControl(true, [Validators.required]),
    productCommentsEnabled: new UntypedFormControl(true, [Validators.required]),
    packingMaterial: new UntypedFormControl('NONE', [Validators.required]),
    visibleFields: this.fb.group({
      companyName: new UntypedFormControl({ value: true, disabled: true }),
      brandName: new UntypedFormControl(false),
      taxNumber: new UntypedFormControl(false),
      email: new UntypedFormControl(false),
      phone: new UntypedFormControl(false),
      businessAddress: new UntypedFormControl(false),
      vatExempt: new UntypedFormControl(false),
      commentsEnabled: new UntypedFormControl(false),
      rankingEnabled: new UntypedFormControl(false),
      gs1Partner: new UntypedFormControl(false),
      packingMaterial: new UntypedFormControl({ value: false, disabled: true }),
      productCommentsEnabled: new UntypedFormControl(false),
    }),
  });

  constructor(
    private readonly store: Store,
    private readonly fb: UntypedFormBuilder,
    private readonly dialogService: DialogService
  ) {}

  onData(data: MerchantBasicDataViewModel) {
    this.form.controls['businessAddress'].reset(); // Telesales-es address beragadás miatt
    let initialFormValue = this.form.value;
    this.form.reset(initialFormValue);
    this.form.patchValue({
      ...data,
      packingMaterial: data.packingMaterial ?? 'NONE',
      visibleFields: {
        ...data.visibleFields,
        companyName: data?.companyName,
        gs1Partner:
          (data?.visibleFields && data?.visibleFields.gs1Partner) ?? false,
        productCommentsEnabled:
          (data?.visibleFields && data?.visibleFields.productCommentsEnabled) ??
          true,
      },
      vatExempt: data?.vatExempt ?? false,
      commentsEnabled: data?.commentsEnabled ?? true,
      rankingEnabled: data?.rankingEnabled ?? true,
      gs1Partner: data?.gs1Partner ?? false,
      productCommentsEnabled: data?.productCommentsEnabled ?? true,
    });
  }

  get(controlName: string): UntypedFormControl {
    return this.form.get(controlName) as UntypedFormControl;
  }

  onToggleVisibleFields() {
    this.showVisibleFields = !this.showVisibleFields;
  }

  getServerError(fieldName: string): any {
    return getServerErrorForField(this.form, fieldName);
  }

  onLogoSelected(file: FileUpload) {
    this.store.dispatch(MerchantActions.updateMerchantLogo.trigger(file));
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.submitData.emit(
        trimInput({ formValue: this.form.value, firstLogin: this.firstLogin })
      );
    }
  }

  onCancel() {
    this.cancel.emit();
  }
}
